import { useState, useEffect, useRef } from 'react'
import { Plus, ArrowRightLeftIcon } from 'lucide-react'
import { ChevronDownIcon, MagnifyingGlassIcon } from '@heroicons/react/20/solid'
import trainingPeaksAppIcon from 'media/integrations/training_peaks/TrainingPeaks_App_Icon.jpg'
import kinesisLogo from 'media/kinesis_logo.png'

import ContentFrame from 'components/Frame/ContentFrame'

export default function TrainingPeaksToKinesisAthleteMappingListItem({
    trainingPeaksAthlete,
    mappedKinesisAthleteUserId,
    kinesisAthletes,
    GetKinesisAthleteDetails = () => {},
    UpdateTrainingPeaksToKinesisAthleteMap = () => {}
}) {

    const dropdownRef = useRef(null)

    const [dropdownOpen, setDropdownOpen] = useState(false)
    const [searchTerm, setSearchTerm] = useState('')
    const [filteredKinesisAthletes, setFilteredKinesisAthletes] = useState(kinesisAthletes)

    const HandleCheckmarkToggle = () => {
        if (mappedKinesisAthleteUserId === 'no-mapping') {
            UpdateTrainingPeaksToKinesisAthleteMap(trainingPeaksAthlete.Id.toString(), 'create-account')
        } else {
            UpdateTrainingPeaksToKinesisAthleteMap(trainingPeaksAthlete.Id.toString(), 'no-mapping')
        }
    }

    const HandleDropdownOpenChange = (value) => {
        setSearchTerm('')
        setFilteredKinesisAthletes(kinesisAthletes)
        setDropdownOpen(value)
    }

    const FilterKinesisAthletesBySearchTerm = (item) => {
        const fullName = item.firstName.toLowerCase() + ' ' + item.lastName.toLowerCase()

        return (
            fullName.includes(searchTerm.toLowerCase()) ||
            item.email.toLowerCase().includes(searchTerm.toLowerCase())
        )
    }

    useEffect(() => {
        if (kinesisAthletes !== null) {
            setFilteredKinesisAthletes(kinesisAthletes.filter(FilterKinesisAthletesBySearchTerm))
        }
    }, [kinesisAthletes, searchTerm])

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                HandleDropdownOpenChange(false)
            }
        }

        document.addEventListener('mousedown', handleClickOutside)

        return () => document.removeEventListener('mousedown', handleClickOutside)
    }, [])

    return (
        <div className="flex items-center gap-x-4">

            <input
                type="checkbox"
                className="h-6 w-6 border-gray-300 rounded cursor-pointer"
                checked={mappedKinesisAthleteUserId !== 'no-mapping'}
                onChange={() => HandleCheckmarkToggle()}
            />
            
            {/* TrainingPeaks Athlete */}
            <div 
                className={`
                    flex-1 
                    ${mappedKinesisAthleteUserId === 'no-mapping' ? 'opacity-75' : ''}
                `}
            >
                <ContentFrame>
                    <div className='w-full flex item-center p-4 gap-x-4'>
                        <img src={trainingPeaksAppIcon} alt="TrainingPeaks App Icon" className="h-10 w-10 rounded-md" />

                        <div className="flex-grow">
                            <p className="font-lg font-semibold">{trainingPeaksAthlete.FirstName} {trainingPeaksAthlete.LastName}</p>
                            <p className="text-sm">{trainingPeaksAthlete?.Email?.length > 0 ? trainingPeaksAthlete.Email : 'No Email'}</p>
                            {/* <p className="text-xxs text-gray-500">{trainingPeaksAthlete.Id}</p> */}
                        </div>
                    </div>
                </ContentFrame>
            </div>

            <div className='w-8 h-8'>
                {mappedKinesisAthleteUserId !== 'no-mapping' && 
                    <ArrowRightLeftIcon className="h-8 w-8 text-gray-400" />
                }
            </div>

            {mappedKinesisAthleteUserId === 'no-mapping' &&
                <div className='flex-1' />
            }

            {/* Kinesis Athlete Dropdown */}
            {mappedKinesisAthleteUserId !== 'no-mapping' &&
                <div className="flex-1 relative" ref={dropdownRef}>

                    {/* Dropdown button */}
                    <ContentFrame>
                        <div
                            type="button"
                            className="w-full flex items-center p-4 gap-x-4 cursor-pointer"
                            onClick={() => HandleDropdownOpenChange(!dropdownOpen)}
                        >
                            {mappedKinesisAthleteUserId === 'create-account' && 
                                <>
                                    <div>
                                        <div className="w-10 h-10 flex items-center justify-center bg-orange-400 rounded-full">
                                            <Plus className="h-6 w-6 text-white" />
                                        </div>
                                    </div>
            
                                    <div className="flex-grow">
                                        <p className="font-semibold">Create Kinesis Athlete Account</p>
                                        <p className="text-sm">TrainingPeaks user does not have a Kinesis account yet</p>
                                    </div>
                                </>
                            }

                            {mappedKinesisAthleteUserId !== 'create-account' && mappedKinesisAthleteUserId !== null && (
                                <>
                                    <img 
                                        src={kinesisLogo} 
                                        alt="Kinesis Logo" 
                                        className="h-10 w-10 rounded-md flex-shrink-0" 
                                    />

                                    <div className="flex-grow">
                                        <p className="font-semibold">
                                            {GetKinesisAthleteDetails(mappedKinesisAthleteUserId).firstName} {GetKinesisAthleteDetails(mappedKinesisAthleteUserId).lastName}
                                        </p>

                                        <p className="text-sm">
                                            {GetKinesisAthleteDetails(mappedKinesisAthleteUserId).email || 'No Email'}
                                        </p>

                                        {/* <p className="text-xxs text-gray-500">{mappedKinesisAthleteUserId}</p> */}
                                    </div>
                                </>
                            )}

                            <div className="flex items-center gap-x-1 flex-shrink-0">
                                <ChevronDownIcon 
                                    className={`
                                        h-8 w-8 text-gray-400 transition-transform flex-shrink-0 
                                        ${dropdownOpen ? 'rotate-180' : ''}
                                    `} 
                                />
                            </div>
                        </div>
                    </ContentFrame>

                    {/* Dropdown Options */}
                    {dropdownOpen && (
                        <div className="absolute z-50 mt-2 w-full rounded-xl border border-gray-200 bg-white shadow-lg overflow-hidden">
                            <div className="max-h-[400px] overflow-auto">
                                
                                {/* Search Input */}
                                <div className="sticky top-0 bg-white border-b border-gray-200 p-2">
                                    <div className="relative">
                                        <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                                            <MagnifyingGlassIcon className="h-4 w-4 text-gray-400" />
                                        </div>

                                        <input
                                            type="text"
                                            className="w-full rounded-md border border-gray-300 py-2 pl-9 pr-3 text-sm text-gray-900 placeholder:text-gray-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                                            placeholder="Search athletes..."
                                            value={searchTerm || ''}
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                            autoFocus
                                        />
                                    </div>
                                </div>

                                {/* Options List */}
                                <div className='divide-y divide-gray-200'>
                                    
                                    <div
                                        className='flex items-center gap-x-3 px-3 py-2 cursor-pointer hover:bg-gray-100'
                                        onClick={() => {
                                            UpdateTrainingPeaksToKinesisAthleteMap(trainingPeaksAthlete.Id.toString(), 'create-account')
                                            HandleDropdownOpenChange(false)
                                        }}
                                    >
                                        <div>
                                            <div className="w-8 h-8 flex items-center justify-center bg-orange-400 rounded-full">
                                                <Plus className="h-4 w-4 text-white" />
                                            </div>
                                        </div>

                                        <div className="flex-grow min-w-0">
                                            <p className="text-sm font-medium text-gray-900 truncate">Create Kinesis Athlete Account</p>
                                            <p className="text-xs text-gray-500 truncate">TrainingPeaks user does not have a Kinesis account yet</p>
                                        </div>
                                    </div>

                                    {filteredKinesisAthletes.map((athlete) => (
                                        <div
                                            key={athlete.id}
                                            className='flex items-center gap-x-3 px-3 py-2 cursor-pointer hover:bg-gray-100'
                                            onClick={() => {
                                                UpdateTrainingPeaksToKinesisAthleteMap(trainingPeaksAthlete.Id.toString(), athlete.id.toString())
                                                HandleDropdownOpenChange(false)
                                            }}
                                        >
                                            <img 
                                                src={kinesisLogo} 
                                                alt="Kinesis Logo" 
                                                className="h-8 w-8 rounded-md flex-shrink-0" 
                                            />
                                            <div className="flex-grow min-w-0">
                                                <p className="text-sm font-medium text-gray-900 truncate">{athlete.firstName} {athlete.lastName}</p>
                                                <p className="text-xs text-gray-500 truncate">{athlete.email || 'No Email'}</p>
                                            </div>
                                        </div>
                                    ))}
                                </div>

                            </div>
                        </div>
                    )}

                </div>
            }
        </div>
    )
}